import { useEffect, useState, useCallback } from "react";
import { useRoomPermissions } from "./useRoomPermissions";
import { getMicrophonePresences } from "../../utils/microphone-presence";
import { ensureNetworkedMicStateSync } from "../../utils/networked-mic-state";
import isEqual from "lodash/isEqual";

export function userFromPresence(sessionId, presence, micPresences, mySessionId, voiceEnabled) {
  const meta = presence.metas[presence.metas.length - 1];
  const microphonePresences = micPresences || new Map();
  const micPresence = microphonePresences.has(sessionId) ? { ...microphonePresences.get(sessionId) } : undefined;

  if (micPresence && !voiceEnabled && !meta.permissions.voice_chat) {
    micPresence.muted = true;
  }

  return {
    id: sessionId,
    isMe: mySessionId === sessionId,
    micPresence,
    ...meta
  };
}

function getPlayerInfoComponents() {
  return window.APP?.componentRegistry?.["player-info"] || [];
}

function getSceneEl() {
  return AFRAME?.scenes?.[0] || null;
}

function subscribeToMicPresenceInvalidation(onInvalidate) {
  const playerInfos = getPlayerInfoComponents();
  const removeRemoteMuteListeners = playerInfos
    .map(playerInfo => {
      const playerInfoEl = playerInfo?.el;

      if (!playerInfoEl?.addEventListener || !playerInfoEl?.removeEventListener) {
        return null;
      }

      playerInfoEl.addEventListener("remote_mute_updated", onInvalidate);

      return () => playerInfoEl.removeEventListener("remote_mute_updated", onInvalidate);
    })
    .filter(Boolean);
  const sceneEl = getSceneEl();

  sceneEl?.addEventListener?.("mic_presence_updated", onInvalidate);

  window.APP?.dialog?.on?.("mic-state-changed", onInvalidate);

  return () => {
    for (const removeListener of removeRemoteMuteListeners) {
      removeListener();
    }

    sceneEl?.removeEventListener?.("mic_presence_updated", onInvalidate);
    window.APP?.dialog?.off?.("mic-state-changed", onInvalidate);
  };
}

export function usePeopleList(presences, mySessionId) {
  const [people, setPeople] = useState([]);
  const { voice_chat: voiceChatEnabled } = useRoomPermissions();

  const calculatePeopleList = useCallback(() => {
    if (!presences) {
      return [];
    }

    const micPresences = getMicrophonePresences();

    const newPeopleList = Object.entries(presences)
      .map(([id, presence]) => {
        if (!presence || !Array.isArray(presence.metas) || presence.metas.length === 0) {
          console.warn(`Invalid presence structure for ID: ${id}`);
          return null;
        }
        return userFromPresence(id, presence, micPresences, mySessionId, voiceChatEnabled);
      })
      .filter(p => p !== null);

    return newPeopleList;
  }, [presences, mySessionId, voiceChatEnabled]);

  useEffect(() => {
    const releaseNetworkedMicStateSync = ensureNetworkedMicStateSync();
    const performUpdate = () => {
      const newPeopleList = calculatePeopleList();

      setPeople(currentPeople => {
        if (!isEqual(currentPeople, newPeopleList)) {
          return newPeopleList;
        }
        return currentPeople;
      });
    };

    const unsubscribeFromMicPresenceInvalidation = subscribeToMicPresenceInvalidation(performUpdate);

    performUpdate();

    return () => {
      unsubscribeFromMicPresenceInvalidation();
      releaseNetworkedMicStateSync();
    };
  }, [calculatePeopleList]);

  return people;
}
